/* Auswahlfeld mit Schnellsuche und optionaler Mini-Vorschau (Combobox): Tippen filtert, ↑/↓ wählen, Enter übernimmt, Esc schließt. */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { quickFilter } from '../../model/assets.ts';

export interface SelectItem { value: string; label: string; /** Zusatz rechts, z. B. Größe oder „fehlt“ */ hint?: string; dim?: boolean; /** kleine Marke vor dem Zusatz, z. B. „Mod“ */ tag?: string }

export function AssetSelect<T extends SelectItem>(props: {
  items: T[];
  ariaLabel: string;
  placeholder?: string;
  /** angezeigter Wert, solange nicht getippt wird */
  value?: string;
  onPick(item: T): void;
  /** Enter ohne Treffer: freien Text übernehmen (z. B. eine Textur, die in keiner WAD steht) */
  onFree?(text: string): void;
  renderPreview?(item: T): ReactNode;
  emptyText?: string;
  /** höchstens so viele Zeilen zeichnen (Standard 60) */
  limit?: number;
}) {
  const id = useId();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [act, setAct] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const hits = useMemo(() => quickFilter(props.items, text, (x) => x.label), [props.items, text]);
  const shown = hits.slice(0, props.limit ?? 60);
  useEffect(() => { setAct(0); }, [text, open]);
  useEffect(() => { (listRef.current?.querySelector('[aria-selected="true"]') as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest' }); }, [act]);

  const pick = (it: T) => { props.onPick(it); setText(''); setOpen(false); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) setOpen(true); else setAct((a) => Math.min(shown.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAct((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && shown[act]) pick(shown[act]);
      else if (text.trim() && props.onFree) { props.onFree(text.trim()); setText(''); setOpen(false); }
    }
    else if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); setText(''); }
  };
  return (
    <div className="asel">
      <input type="text" className="inp" role="combobox" aria-label={props.ariaLabel} aria-expanded={open} aria-controls={id} aria-autocomplete="list" aria-activedescendant={open && shown[act] ? id + '-' + act : undefined}
        autoComplete="off" spellCheck={false} placeholder={props.placeholder} value={open ? text : (text || props.value || '')}
        onFocus={() => setOpen(true)} onBlur={() => { setOpen(false); setText(''); }}
        onChange={(e) => { setText(e.target.value); setOpen(true); }} onKeyDown={onKey} />
      {open && (
        <div className="asel-list" role="listbox" id={id} ref={listRef} onMouseDown={(e) => e.preventDefault()}>
          {!shown.length && <div className="asel-empty small">{props.emptyText ?? '–'}</div>}
          {shown.map((it, i) => (
            <div key={it.value} id={id + '-' + i} role="option" aria-selected={i === act} className={'asel-opt' + (i === act ? ' act' : '') + (it.dim ? ' dim' : '')}
              onMouseEnter={() => setAct(i)} onClick={() => pick(it)}>
              {props.renderPreview && <span className="asel-pv">{props.renderPreview(it)}</span>}
              <span className="asel-lbl">{it.label}</span>
              {it.tag && <span className="asel-tag">{it.tag}</span>}
              {it.hint && <span className="asel-hint small">{it.hint}</span>}
            </div>
          ))}
          {hits.length > shown.length && <div className="asel-empty small">+{hits.length - shown.length}</div>}
        </div>
      )}
    </div>
  );
}
